import { useState } from "react";

/**
 * A line asking somebody to update the application on her phone.
 *
 * Nineteen iPhones registered no push token for a fortnight and every one of them looked,
 * from the server, exactly like a phone that worked. The fix is in a newer build, and a
 * teacher who never opens the store will never get it — automatic updates cover most
 * people and this is for the rest.
 *
 * Everything about whether to show it was decided before the document left the Worker, and
 * arrives as one attribute on the root element. Nothing is asked here, nothing is guessed
 * from a User-Agent in the browser, and the attribute is in the markup before React runs:
 * the last mechanism that decided this in a script failed silently for a fortnight.
 *
 * **It is not a link.** The installed application is confined to its own domain — that
 * confinement is what lets passkeys work inside it — so a store link would do nothing at
 * all on the phones this is for. Instructions a person can follow are worth more than a
 * button that quietly fails.
 *
 * **It names her own phone.** This said "This iPhone" and "the App Store" to everybody,
 * including fifty-nine people holding Android phones, who were told to open a shop that is
 * not on their device. The Worker already marks the document with which shell it served,
 * so the line can be right rather than nearly right.
 */
export function UpdateTheApplication({
  behind = behindTheStore(),
  platform = whichPhone(),
}: { behind?: boolean; platform?: "ios" | "android" | null } = {}) {
  const [dismissed, setDismissed] = useState(() => putAwayToday());
  if (dismissed || !behind) return null;
  const store = platform === "android" ? "Play Store" : "App Store";
  const phone = platform === "android" ? "phone" : "iPhone";
  return (
    <aside className="update-app" role="status">
      <p>
        <strong>Update WVCEA Connect.</strong> This {phone} is running an older version.
        Open the {store}, search for WVCEA Connect, and tap Update — a recording made on the
        older one may never reach us.
      </p>
      {/*
        Gone for the rest of the day rather than until the next page.
        It was held in memory, so switching tabs brought it back, and each tab loads its own
        page: a teacher who had already updated met the same line all morning with no way to
        put it down. Kept per day, so somebody who still has not updated tomorrow sees it
        again.
      */}
      <button type="button" onClick={() => { rememberPutAway(); setDismissed(true); }} aria-label="Hide this for today">
        Not now
      </button>
    </aside>
  );
}

/**
 * What the Worker decided about this phone, read from the document it served.
 *
 * Taken as a prop with this as its default so the rule and the reading can be tested apart:
 * the tests run without a document, and a decision that can only be exercised through a
 * browser is a decision nobody checks.
 */
function behindTheStore(): boolean {
  return typeof document !== "undefined"
    && document.documentElement.hasAttribute("data-shell-behind");
}

/** Which shell served this document, from the same mark. Null in a browser. */
function whichPhone(): "ios" | "android" | null {
  if (typeof document === "undefined") return null;
  const said = document.documentElement.getAttribute("data-shell");
  return said === "android" || said === "ios" ? said : null;
}

const PUT_AWAY_KEY = "wvcea.update-banner.put-away";

/** Today where she is, so the line comes back on a new convention morning. */
function today(): string {
  return new Date().toDateString();
}

function putAwayToday(): boolean {
  try {
    return window.localStorage.getItem(PUT_AWAY_KEY) === today();
  } catch {
    // A phone with storage blocked still gets the line; it just cannot put it down.
    return false;
  }
}

function rememberPutAway(): void {
  try {
    window.localStorage.setItem(PUT_AWAY_KEY, today());
  } catch {
    // Nothing here is worth interrupting a reader for.
  }
}
